import type { AttackDef, Boss, Player } from './types'
import type { BossAttackTimeline } from './boss_pose_timeline'
import { drawMat, dustReady, dustSprite, preloadExecFxMats, tinted } from './exec_fx_mats'

// Adopted 2026-09-24. Historical study names remain for the DEV before/after lab.
// Production always uses the accepted motion; only DEV can select the previous motion.
let enabled = true
let continuousContact = true
function motionEnabled(): boolean { return !import.meta.env.DEV || enabled }
export function setAwokenRushStudy(enabledValue: boolean): void { enabled = enabledValue }
export function setAwokenRushContinuousContact(value: boolean): void { continuousContact = value }
// Legacy contact pause is available only when explicitly comparing the study.
export function restoresAwokenRushContactStopForStudy(): boolean {
  return enabled && !continuousContact
}
export function isAwokenRushStudy(boss: Boss): boolean {
  return motionEnabled() && boss.id === 'crowned-king-awoken' && boss.currentAttack?.id === 'feralRush'
}
export const AWOKEN_CHASE_CUE_MS = 217
export function isAwokenChaseStudy(boss: Boss): boolean {
  return motionEnabled() && boss.id === 'crowned-king-awoken' && boss.currentAttack?.id === 'feralRushChase'
}
export function isAwokenRushOpening(boss: Boss): boolean {
  return isAwokenRushStudy(boss) || (isAwokenChaseStudy(boss) && boss.comboHitIndex === 0)
}
export function isAwokenRushRelease(boss: Boss): boolean {
  return (isAwokenRushStudy(boss) && boss.state === 'recovery')
    || (isAwokenChaseStudy(boss) && boss.state === 'comboGap'
      && boss.stateElapsed * 1000 < AWOKEN_CHASE_CUE_MS)
}

export function awokenRushStudyTarget(boss: Boss, player: Player, attack: AttackDef): number | undefined {
  if (!motionEnabled() || boss.id !== 'crowned-king-awoken') return undefined
  // Second-hit target is computed before comboHitIndex increments: phase is essential.
  if (attack.id !== 'feralRush'
    && !(attack.id === 'feralRushChase' && boss.state === 'telegraph')) return undefined
  const dir = boss.facing === 'right' ? 1 : -1
  // Commit once at launch; never chase a player who dodges behind afterwards.
  // Stop 110px beyond the launch-time target, with a bounded run-up.
  const distance = Math.max(110, Math.min(430, (player.x - boss.x) * dir + 110))
  return boss.x + dir * distance
}

export function awokenRushStudyAttack(original: AttackDef): AttackDef {
  return {
    ...original,
    passThroughBody: true,
    activeMs: 260,
    // Accelerate, carry the body through, then brake during the last third.
    // Preserve boss-following hit / dodge-counter semantics (not positionEvadable).
    forwardWarp: [0, 0.06, 0.20, 0.38, 0.58, 0.76, 0.88, 0.95, 0.985, 1],
  }
}

// 突進のブレーキの足元 (2026-10-29・作者の答え exec-fx-q5 のメモ「前に突進する攻撃…足元の…線を引いたようなエフェクト」)。
// 既定 = 描いた土ぼこりの絵 (exec_fx_mats.ts の dust) を爪の前へ蹴り出す (10-30 作者 exec-fx の答え ak-rush・ak-chase「入れる」で配布版へ)。前の版 (爪の筋＋丸いぼかし＋四角いかけら) は DEV ?awokenBrake=line。
export type AwokenBrakeLook = 'dust' | 'line'
let brakeLook: AwokenBrakeLook = 'dust'
export function setAwokenBrakeLook(value: string | null | undefined): void {
  brakeLook = value === 'line' ? 'line' : 'dust'
  if (brakeLook === 'dust') preloadExecFxMats()  // 最初の突進に素材が間に合うよう、戦いの始めに読む
}
const BRAKE_MS = 420
const BRAKE_DUST_RGB = '196,182,160'
const brakeFrame = (t: number): number => Math.min(3, Math.floor(t * 4))
const brakeFade = (t: number): number => Math.min(1, t / 0.08) * (t > 0.45 ? 1 - (t - 0.45) / 0.55 : 1)

// Stateless, phase-clock FX: pause/slow motion/replay cannot leave old dust alive.
// Points are the two front claws projected from the actual sprite placement. h = 絵の表示の高さ (大きさの物差し)。
export function drawAwokenRushBrake(
  ctx: CanvasRenderingContext2D, boss: Boss,
  feet: readonly { x: number; y: number }[], visibility: number, h = 0,
): void {
  if (!isAwokenRushOpening(boss) || boss.hp <= 0 || visibility <= 0) return
  const chase = isAwokenChaseStudy(boss)
  if (chase && boss.state === 'comboGap') {
    // Keep the same initial brake, then disperse before the visible turn cue.
    visibility *= Math.max(0, Math.min(1, (AWOKEN_CHASE_CUE_MS - boss.stateElapsed * 1000) / 57))
    if (visibility <= 0) return
  }
  const activeMs = boss.currentAttack!.activeMs
  const brakeStart = activeMs * 0.76
  const age = boss.state === 'active' ? boss.stateElapsed * 1000 - brakeStart
    : isAwokenRushRelease(boss) ? activeMs - brakeStart + boss.stateElapsed * 1000 : -1
  if (age < 0 || age >= BRAKE_MS) return
  const dir = boss.facing === 'left' ? -1 : 1
  if (brakeLook === 'dust' && h > 0) {
    preloadExecFxMats()
    if (dustReady()) drawBrakeDust(ctx, feet, visibility, age, dir, h)
    return
  }
  const fade = Math.min(1, age / 12) * Math.pow(1 - age / 420, 1.2)
  ctx.save()
  ctx.lineCap = 'round'
  feet.forEach((foot, index) => {
    // Parallel claw scores trail behind the planted paw, flat on the floor.
    for (let line = 0; line < 3; line++) {
      ctx.globalAlpha = visibility * fade * (index === 0 ? 0.65 : 0.9)
      ctx.strokeStyle = line === 1 ? '#e2cba3' : '#9d8c76'
      ctx.lineWidth = line === 1 ? 2.4 : 3.2
      const y = foot.y + line * 3 - 3
      ctx.beginPath()
      ctx.moveTo(foot.x - dir * (20 + Math.min(age, 65) * 0.72), y)
      ctx.lineTo(foot.x + dir * 3, y)
      ctx.stroke()
    }
    // A low fan of floor dust rolls in front of the claws; no magical glow.
    for (let i = 0; i < 7; i++) {
      const t = (age - i * 7) / 360
      if (t <= 0 || t >= 1) continue
      const x = foot.x + dir * (6 + t * (42 + i * 5))
      const y = foot.y - Math.sin(t * Math.PI * 0.8) * (16 + i % 3 * 9)
      const radius = 8 + t * (15 + i % 3 * 3)
      const dust = ctx.createRadialGradient(x, y, 0, x, y, radius)
      dust.addColorStop(0, '#d1bfa3')
      dust.addColorStop(0.4, 'rgba(184,165,140,0.8)')
      dust.addColorStop(1, 'rgba(140,126,110,0)')
      // Show the braking burst immediately, then let it disperse.
      ctx.globalAlpha = visibility * Math.min(1, t / 0.12) * (1 - t)
        * (index === 0 ? 0.62 : 0.88)
      ctx.fillStyle = dust
      ctx.beginPath()
      ctx.ellipse(x, y, radius * 1.45, radius, 0, 0, Math.PI * 2)
      ctx.fill()
    }
    // Small stone chips give the first instant a sharper bite than smoke alone.
    for (let i = 0; i < 5; i++) {
      const t = age / 280
      if (t >= 1) continue
      ctx.globalAlpha = visibility * (1 - t) * 0.8
      ctx.fillStyle = i % 2 ? '#c7b694' : '#8d8274'
      const x = foot.x + dir * (5 + t * (38 + i * 9))
      const y = foot.y - Math.sin(t * Math.PI) * (12 + i % 3 * 10)
      ctx.fillRect(x, y, 3 + i % 2, 2)
    }
  })
  ctx.restore()
}

// 爪の前へ蹴り出す土ぼこり (絵は右へ広がる向き)＋かけら。後ろの足は薄く小さく。
function drawBrakeDust(
  ctx: CanvasRenderingContext2D, feet: readonly { x: number; y: number }[],
  visibility: number, age: number, dir: number, h: number,
): void {
  const t = age / BRAKE_MS
  feet.forEach((foot, index) => {
    const front = index === feet.length - 1
    const size = h * (front ? 0.5 : 0.36)
    const kick = dustSprite('kick', brakeFrame(t))
    if (kick) {
      drawMat(ctx, tinted(kick, BRAKE_DUST_RGB), foot.x - dir * size * 0.08, foot.y, size,
        size * kick.naturalHeight / kick.naturalWidth, 0.18, 0.98,
        visibility * brakeFade(t) * (front ? 0.9 : 0.6), 0, dir < 0)
    }
    // 爪が床を噛んだ最初の一瞬だけ、かけらが前へ跳ねる。
    const tb = age / 260
    const bit = tb < 1 ? dustSprite('bit', index * 2 + (tb > 0.5 ? 1 : 0)) : null
    if (bit) {
      const w = size * 0.55
      drawMat(ctx, tinted(bit, BRAKE_DUST_RGB), foot.x + dir * size * (0.15 + tb * 0.35), foot.y, w,
        w * bit.naturalHeight / bit.naturalWidth, 0.5, 0.98,
        visibility * brakeFade(tb) * (front ? 0.85 : 0.55), 0, dir < 0)
    }
  })
}

// Existing art is a motion placeholder. A constant pose-size correction is not
// animated squash. Small rotations pivot at the feet so the head moves most.
const brace = { x: 1.10, y: 1.10 }
export const AWOKEN_RUSH_STUDY_TIMELINE: BossAttackTimeline = [
  { phase: 'telegraph', at: 0, image: 'idle', hold: true, ease: 'step' },
  ...Array.from({ length: 15 }, (_, i) => ({
    phase: 'telegraph' as const, at: 0.18 + i * 0.055,
    image: 'rush_windup', hold: true, scale: brace,
    rot: (i % 2 ? 1 : -1) * (0.28 + i * 0.025),
    ease: 'easeInOut' as const,
  })),
  { phase: 'telegraph', at: 0.98, image: 'rush_windup', hold: true, scale: brace, rot: 0, ease: 'step' },
  { phase: 'active', at: 0, image: 'rush_lunge', hold: true, tag: 'contact', offset: { y: -4 } },
  { phase: 'active', at: 0.64, image: 'rush_lunge', hold: true, offset: { y: 0 }, ease: 'step' },
  { phase: 'active', at: 0.76, image: 'rush_windup', hold: true, scale: brace, rot: 2, ease: 'easeOut' },
  { phase: 'active', at: 1, image: 'rush_windup', hold: true, scale: brace, rot: 0.8 },
  // Carry the visible fangs across the active/recovery boundary, then release
  // them over 160ms using the matching toothless drawing of the same pose.
  { phase: 'recovery', at: 0, image: 'rush_windup_fangs_0917', hold: false, scale: brace, rot: 0.8 },
  { phase: 'recovery', at: 0.20, image: 'rush_windup', hold: true, scale: brace, rot: 0.3 },
  { phase: 'recovery', at: 0.3, image: 'rush_windup', hold: true, scale: brace, rot: 0, ease: 'step' },
  { phase: 'recovery', at: 0.62, image: 'bite_recover', hold: true, tag: 'recover' },
]

export function awokenChaseStudyTimeline(original: BossAttackTimeline): BossAttackTimeline {
  return [
    ...AWOKEN_RUSH_STUDY_TIMELINE.filter(k => k.phase === 'telegraph' || k.phase === 'active')
      .map(k => k.phase === 'active' ? { ...k, hitIndex: 0 } : { ...k }),
    ...AWOKEN_RUSH_STUDY_TIMELINE.filter(k => k.phase === 'recovery' && k.at <= 0.20)
      // The combo has not ended: preserve the brake motion, but never release its fangs.
      .map(k => ({ ...k, phase: 'comboGap' as const, at: k.at * 800 / 620,
        image: 'rush_windup_fangs_0917', hold: true })),
    // Existing coil and second bite: keep the 403ms warning and 1150ms final opening.
    ...original.filter(k => (k.phase === 'comboGap' && k.at >= 0.35)
      || (k.phase === 'active' && k.hitIndex === 1)
      // Hold the planted bite follow-through until recovery; do not fall forward
      // into the old rush-overextension drawing after the second bite.
      || (k.phase === 'recovery' && k.image !== 'rush_overextend'))
      .map(k => k.image === 'chase_coil' ? { ...k, image: 'chase_coil_fangs_0924' } : { ...k }),
  ]
}
